import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import collectionsData from '../data/collections.json';
import { stories } from '../data/lookbook';
import { runwayMeta, runwayPhotos } from '../data/runway';
import { site, whatsappEnquiry } from '../data/site';
import { Icon, Ornament, Reveal, SectionHeading } from '../components/Primitives';
import ProductCard from '../components/ProductCard';
import Seo from '../components/Seo';
import { AssurancePanel, CtaBand } from '../components/Shared';

const { collections, products } = collectionsData;

/* Hero rotates through the three shoots so the fold shows the range. */
const HERO_SLIDES = [
  {
    src: '/assets/lookbook/sapphire-01.webp',
    alt: 'Bride in a royal blue lehenga wearing a layered polki and ruby-bead bridal suite',
    pos: 'object-[50%_22%]',
  },
  {
    src: '/assets/lookbook/ivory-08.webp',
    alt: 'Model in ivory chikankari wearing an emerald and pearl polki choker',
    pos: 'object-[50%_18%]',
  },
  {
    src: '/assets/lookbook/sapphire-w-01.webp',
    alt: 'Close frame of a polki choker, chandbali and nath',
    pos: 'object-[52%_35%]',
  },
];

function Hero() {
  const [i, setI] = useState(0);

  useEffect(() => {
    const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return undefined;
    const t = setInterval(() => setI((n) => (n + 1) % HERO_SLIDES.length), 6500);
    return () => clearInterval(t);
  }, []);

  return (
    /* Sized to fill what's left of the fold under the sticky header. */
    <section className="relative min-h-[calc(100svh-64px)] overflow-hidden bg-royal-950 sm:min-h-[calc(100svh-72px)]">
      {HERO_SLIDES.map((slide, n) => (
        <div
          key={slide.src}
          className={`absolute inset-0 transition-opacity duration-[1600ms] ease-luxe ${
            n === i ? 'opacity-100' : 'opacity-0'
          }`}
          aria-hidden={n !== i}
        >
          <img
            src={slide.src}
            alt={n === 0 ? slide.alt : ''}
            fetchpriority={n === 0 ? 'high' : undefined}
            loading={n === 0 ? 'eager' : 'lazy'}
            decoding="async"
            className={`h-full w-full object-cover ${slide.pos} ${
              n === i ? 'animate-ken-burns' : ''
            }`}
          />
        </div>
      ))}

      {/*
        Legibility scrim. The models are centred in frame and the images are no
        wider than the viewport, so object-position can't pan them out from under
        the copy — the gradient has to do the work. Explicit stops keep it opaque
        behind the headline and clear of the jewellery on the right.
      */}
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-[linear-gradient(100deg,rgba(16,15,32,0.97)_0%,rgba(16,15,32,0.88)_30%,rgba(16,15,32,0.55)_52%,rgba(16,15,32,0.18)_78%,rgba(16,15,32,0.10)_100%)]"
      />
      <div
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-royal-950 via-royal-950/55 to-transparent"
      />

      <div className="shell relative flex min-h-[calc(100svh-64px)] items-center py-16 sm:min-h-[calc(100svh-72px)] sm:py-20">
        <div className="max-w-2xl">
          <Reveal>
            <p className="eyebrow-light">Ahmedabad · Since {site.since}</p>
          </Reveal>

          <Reveal delay={90}>
            <h1 className="display-xl mt-5 text-cream-50">
              Heirlooms for the
              <span className="block italic text-gold-300">generation ahead</span>
            </h1>
          </Reveal>

          <Reveal delay={180}>
            <Ornament tone="light" className="mt-7 !justify-start" />
          </Reveal>

          <Reveal delay={250}>
            <p className="mt-7 max-w-xl text-[15px] leading-relaxed text-cream-100/80 sm:text-[17px]">
              Seventy-five years of closed-set kundan and polki from a single family
              on C G Road — supplying brides across Gujarat, and the jewellers who
              dress them.
            </p>
          </Reveal>

          <Reveal delay={330}>
            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link to="/collections" className="btn-gold">
                Explore collections
                <Icon.Arrow className="h-3.5 w-3.5" />
              </Link>
              <Link to="/visit" className="btn-outline-light">
                Visit the showroom
              </Link>
            </div>
          </Reveal>

          <Reveal delay={420}>
            <dl className="mt-14 grid max-w-lg grid-cols-3 gap-6 border-t border-cream-50/15 pt-7">
              {[
                ['75+', 'Years of trade'],
                ['3', 'Generations'],
                ['1951', 'Established'],
              ].map(([big, small]) => (
                <div key={small}>
                  <dt className="font-display text-3xl font-light text-gold-300">{big}</dt>
                  <dd className="mt-1 text-[11px] uppercase tracking-wide2 text-cream-200/60">
                    {small}
                  </dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-7 right-5 z-10 flex items-center gap-2 sm:right-12">
        {HERO_SLIDES.map((slide, n) => (
          <button
            key={slide.src}
            type="button"
            aria-label={`Show image ${n + 1}`}
            aria-current={n === i}
            onClick={() => setI(n)}
            className={`h-px transition-all duration-500 ease-luxe ${
              n === i ? 'w-10 bg-gold-400' : 'w-5 bg-cream-50/35 hover:bg-cream-50/60'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- collections */
function CollectionsPreview() {
  const featured = collections.slice(0, 3);

  return (
    <section className="bg-cream-50 py-20 sm:py-28">
      <div className="shell">
        <SectionHeading
          eyebrow="The Collections"
          title="Chosen the way a family chooses"
          lede="Suites built to be worn together — collar, haar, chandbali, kada — then broken up and worn again long after the wedding."
        />

        <div className="mt-14 grid gap-8 md:grid-cols-3">
          {featured.map((c, i) => (
            <Reveal key={c.slug} delay={i * 110}>
              <Link to={`/collections/${c.slug}`} className="group block">
                <div className="relative overflow-hidden bg-royal-800 ring-1 ring-inset ring-gold-500/20">
                  <div className="aspect-[4/5] overflow-hidden">
                    <img
                      src={`/assets/lookbook/${c.hero}.webp`}
                      alt={`${c.name} — worn`}
                      loading="lazy"
                      decoding="async"
                      /* Model frames, cropped from the top so the face and the
                         jewellery stay in frame while the caption takes the base. */
                      className="h-full w-full object-cover object-[center_18%]
                        transition-transform duration-[1200ms] ease-luxe group-hover:scale-[1.05]"
                    />
                  </div>
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t
                      from-royal-950 via-royal-950/70 to-transparent"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <p className="eyebrow-light">{c.eyebrow}</p>
                    <h3 className="mt-2 font-display text-2xl font-normal text-cream-50">
                      {c.name}
                    </h3>
                    <p className="mt-2 text-[13.5px] leading-relaxed text-cream-200/75">
                      {c.tagline}
                    </p>
                    <span className="mt-4 inline-flex items-center gap-2 text-[11px] uppercase tracking-wide2 text-gold-300">
                      View collection
                      <Icon.Arrow className="h-3.5 w-3.5 transition-transform duration-500 ease-luxe group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 text-center">
          <Link to="/collections" className="btn-outline">
            All six collections
            <Icon.Arrow className="h-3.5 w-3.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------- signature */
function SignaturePieces() {
  /*
   * All four are plates the pipeline flooded to flat brand navy, so they sit on a
   * bg-royal-800 card with no visible seam. Pieces shot on the client's
   * diagonal-textured navy (the navy-* set) are deliberately not used here — their
   * texture reads as a floating square against a flat card.
   */
  const picks = ['polki-bridal-collar', 'polki-chandbali', 'ruby-pearl-collar', 'ruby-jhumka'].map(
    (id) => products.find((p) => p.id === id)
  );

  return (
    <section className="noise-navy relative overflow-hidden py-20 sm:py-28">
      <div className="shell relative">
        <SectionHeading
          tone="light"
          eyebrow="Signature Pieces"
          title="Closed-set, by hand, in Ahmedabad"
          lede="Polki is uncut diamond backed with gold foil, so light comes back off the metal rather than through the stone. It is the oldest setting we work in — and still the slowest."
        />

        <div className="mt-14 grid gap-x-7 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
          {picks.map((p, i) => (
            <Reveal key={p.id} delay={i * 90}>
              <article className="group">
                {/*
                  The pipeline centres every piece on a square navy canvas, so the
                  plate is square too — a taller arch frame left a bare navy band
                  under each piece, and masking a dome over a wide collar clipped
                  its top corners. Square clips nothing and keeps the row uniform.
                */}
                <div className="flex aspect-square items-center justify-center">
                  <div
                    className="relative aspect-square w-full overflow-hidden bg-royal-800
                      ring-1 ring-inset ring-gold-500/20"
                  >
                    <img
                      src={`/assets/products/${p.image}.webp`}
                      alt={p.name}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-contain transition-transform
                        duration-[1100ms] ease-luxe group-hover:scale-[1.05]"
                    />
                  </div>
                </div>
                <div className="mt-5 text-center">
                  <h3 className="font-display text-xl font-normal text-cream-50">{p.name}</h3>
                  <p className="mx-auto mt-2 max-w-xs text-[13px] leading-relaxed text-cream-200/60">
                    {p.blurb}
                  </p>
                </div>
              </article>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14 text-center">
          <a
            href={whatsappEnquiry('Signature polki pieces')}
            target="_blank"
            rel="noreferrer noopener"
            className="btn-gold"
          >
            <Icon.WhatsApp className="h-4 w-4" />
            Ask about a piece
          </a>
        </Reveal>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- silk row */
function SilkEdit() {
  const silk = products.filter((p) => p.shape === 'silk').slice(0, 4);

  return (
    <section className="bg-cream-200 py-20 sm:py-28">
      <div className="shell">
        <div className="grid items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <SectionHeading
              align="left"
              eyebrow="Beyond the Bridal"
              title="Emerald, jade and coral — for the days after"
              lede="Lighter sets in coloured stone and enamel, made to be worn to a mehndi, a reception, or a Tuesday."
            />
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link to="/collections/necklaces" className="btn-outline">
              Browse necklaces
              <Icon.Arrow className="h-3.5 w-3.5" />
            </Link>
          </div>
        </div>

        <div className="mt-14 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
          {silk.map((p, i) => (
            <Reveal key={p.id} delay={i * 80}>
              <ProductCard product={p} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------ lookbook tease */
function LookbookTease() {
  return (
    <section className="bg-cream-50 py-20 sm:py-28">
      <div className="shell">
        <SectionHeading
          eyebrow="The Lookbook"
          title="Three stories, one workshop"
          lede="The same stones photographed three ways — a sapphire bridal suite, emerald and pearl on ivory, and a single medallion on black."
        />

        <div className="mt-14 grid gap-7 md:grid-cols-3">
          {stories.map((s, i) => (
            <Reveal key={s.slug} delay={i * 110}>
              <Link to={`/lookbook#${s.slug}`} className="group block">
                <div className="relative overflow-hidden">
                  <div className="aspect-[3/4] overflow-hidden bg-royal-900">
                    <img
                      src={s.cover.src}
                      alt={s.cover.alt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform duration-[1300ms]
                        ease-luxe group-hover:scale-[1.06]"
                    />
                  </div>
                  <div
                    aria-hidden="true"
                    className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t
                      from-royal-950 via-royal-950/70 to-transparent"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-7">
                    <p className="eyebrow-light">{s.eyebrow}</p>
                    <h3 className="mt-2 font-display text-3xl font-light text-cream-50">
                      {s.title}
                    </h3>
                    <p className="mt-1.5 text-[13.5px] text-cream-200/75">{s.subtitle}</p>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 text-center">
          <Link to="/lookbook" className="btn-primary">
            Open the lookbook
            <Icon.Arrow className="h-3.5 w-3.5" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------------------------------------------------------- runway */
/*
 * Full-bleed navy band between the two cream galleries. The show frames are dark
 * and lit from behind, so they'd fight a cream card; giving them a navy stage and
 * letting one wide frame run edge to edge reads closer to how the evening looked.
 */
function RunwayStrip() {
  const strip = runwayPhotos.filter((p) => !p.wide).slice(0, 4);

  return (
    <section className="noise-navy relative overflow-hidden py-20 sm:py-28">

      <div className="shell relative">
        <div className="grid gap-10 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-7">
            <p className="eyebrow-light">{runwayMeta.eyebrow}</p>
            <h2 className="display-lg mt-4 text-cream-50">
              Our pieces, walking
              <br />
              a couture ramp
            </h2>
            <Ornament tone="light" className="mt-6 !justify-start" />
            <p className="mt-6 max-w-xl text-[15px] leading-relaxed text-cream-200/75 sm:text-[16.5px]">
              {runwayMeta.lede}
            </p>
            <Link to="/runway" className="btn-gold mt-9">
              See the show
              <Icon.Arrow className="h-3.5 w-3.5" />
            </Link>
          </Reveal>

          <Reveal delay={120} className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-3">
              {strip.map((p) => (
                <Link
                  key={p.src}
                  to="/runway"
                  className="group block overflow-hidden bg-royal-950 ring-1 ring-inset ring-gold-500/20"
                >
                  <div className="aspect-[2/3] overflow-hidden">
                    <img
                      src={p.thumb}
                      alt={p.alt}
                      loading="lazy"
                      decoding="async"
                      className="h-full w-full object-cover transition-transform
                        duration-[1300ms] ease-luxe group-hover:scale-[1.07]"
                    />
                  </div>
                </Link>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- heritage */
function HeritageStrip() {
  return (
    <section className="relative overflow-hidden bg-cream-100">
      <div className="grid lg:grid-cols-2">
        <div className="relative min-h-[22rem] lg:min-h-[34rem]">
          <img
            src="/assets/lifestyle/lifestyle-04.webp"
            alt="A customer trying gold and polki jewellery at the Nikhil Jewellers counter on C G Road"
            loading="lazy"
            decoding="async"
            /* Focus the customer; centring the crop cuts the signage mid-word. */
            className="absolute inset-0 h-full w-full object-cover object-[34%_46%]"
          />
        </div>

        <div className="flex items-center px-5 py-16 sm:px-10 lg:px-16 lg:py-24">
          <div className="max-w-xl">
            <Reveal>
              <p className="eyebrow">Our Heritage</p>
              <h2 className="display-lg mt-4 text-royal-900">
                One family, one counter, since 1951
              </h2>
              <Ornament className="mt-6 !justify-start" />
              <p className="mt-7 lede">
                Nikhil Jewellers began as a small counter on C G Road and grew into
                the showroom it is today. The work has stayed the same throughout:
                closed-set kundan and polki, cut and finished by karigars we have
                worked with for decades.
              </p>
              <p className="mt-4 lede">
                Three generations have stood behind the same counter, which is why
                the pricing is plain and the advice is straight.
              </p>

              <div className="mt-9 flex items-center gap-4">
                <img
                  src="/assets/team/nikhil-soni.webp"
                  alt="Nikhil Soni"
                  loading="lazy"
                  decoding="async"
                  className="h-16 w-16 rounded-full object-cover object-top ring-1 ring-gold-500/40"
                />
                <div>
                  <p className="font-display text-lg text-royal-900">Nikhil Soni</p>
                  <p className="text-[12px] uppercase tracking-wide2 text-royal-700/60">
                    Proprietor
                  </p>
                </div>
              </div>

              <div className="mt-9">
                <Link to="/heritage" className="btn-outline">
                  Read our story
                  <Icon.Arrow className="h-3.5 w-3.5" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ----------------------------------------------------------------- page */
export default function Home() {
  return (
    <>
      <Seo
        description="Nikhil Jewellers, Ahmedabad — kundan, polki and diamond jewellery since 1951. Bridal suites, necklaces, earrings, bangles and rings on C G Road."
      />
      <Hero />
      <AssurancePanel />
      <CollectionsPreview />
      <SignaturePieces />
      <SilkEdit />
      <LookbookTease />
      <RunwayStrip />
      <HeritageStrip />
      <CtaBand />
    </>
  );
}
